import {useState, useEffect} from 'react';

import {useTranslation} from 'react-i18next';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {Autocomplete, Box, Checkbox, Chip, SxProps, TextField, Typography, useTheme} from '@mui/material';

import {orangeColors} from '../../layouts/theme/designSystem';
import {getFieldStyle} from '../../layouts/theme/FieldStyles';
import {Option} from '../../types/shared-types';

interface AutoCompleteFilterProps {
  options: Option[];
  onSelect: (value: Option[]) => void;
  currentOptions?: Option[];
  sx?: SxProps;
  label?: string;
}

export const AutoCompleteFilter = ({options, onSelect, currentOptions, sx, label}: AutoCompleteFilterProps) => {
  const theme = useTheme();
  const {t} = useTranslation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!currentOptions?.length) return;
    setInitialized(true);

    setSelectedIds(currentOptions.map((option) => option.id));

    //This should only happen once
    //eslint-disable-next-line
  }, [currentOptions, initialized]);

  const handleSelect = (_event: React.SyntheticEvent, selectedOptions: Option[]) => {
    onSelect(selectedOptions);
    setSelectedIds(selectedOptions.map((o) => o.id));
  };
  const renderTags = (selectedOptions: Option[]) => (
    <Chip
      size='small'
      label={
        <Box display='flex' alignItems='center' gap={0.5}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: orangeColors[3],
            }}
          >
            <Typography component='span' variant='caption' sx={{lineHeight: 1, fontSize: '0.75rem'}}>
              {selectedOptions.length}
            </Typography>
          </Box>
          <Typography
            component='span'
            variant='caption'
            sx={{lineHeight: 1, fontSize: '0.75rem', whiteSpace: 'nowrap'}}
          >
            {t('shared.selected')}
          </Typography>
        </Box>
      }
      sx={{background: 'transparent', height: 24, maxHeight: 24, '& .MuiChip-label': {px: 0.5}}}
    />
  );

  const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize='small' />;
  const checkboxCheckedIcon = <CheckBoxIcon fontSize='small' color='action' />;

  const fieldStyles = getFieldStyle(theme);
  const outlinedInputRoot = (fieldStyles as Record<string, object>)['& .MuiOutlinedInput-root'] ?? {};

  return (
    <Autocomplete
      options={options}
      multiple
      size='small'
      disableCloseOnSelect
      limitTags={1}
      value={options.filter((option) => selectedIds.includes(option.id))}
      onChange={handleSelect}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.label}
      renderTags={renderTags}
      data-testid='auto-complete-checkbox-filter'
      sx={{
        ...fieldStyles,
        '& .MuiOutlinedInput-root': {
          ...outlinedInputRoot,
          flexWrap: 'nowrap',
          alignItems: 'center',
        },
        '& .MuiAutocomplete-input': {
          minWidth: 24,
          alignSelf: 'center',
        },
        ...sx,
      }}
      ListboxProps={{style: {maxHeight: 250}}}
      renderOption={(props, option, {selected}) => (
        <li {...props} key={option.id}>
          <Checkbox icon={checkboxIcon} checkedIcon={checkboxCheckedIcon} style={{marginRight: 8}} checked={selected} />
          {option.label}
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={label} size='small' fullWidth />}
    />
  );
};
